import { isNull, isUndefined, uniq } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { UTC_FORMAT, formatDate } from '../../../../utils/luxon';
import { getAllDropdownLists } from '../../../admin/dropdown-lists/store';
import { FeaturePermission } from '../../../admin/users/store';
import { getUserHasFeaturePermissionNoAdmin } from '../../../auth/login/store';
import { getFocusOptions, opinionCommissionedByOptions } from '../../../constants/opinion';
import { DatePicker } from '../../../shared/datepicker/DatePicker';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Text } from '../../../shared/text/Text';
import { ArkOpinion, fetchSourceOpinionsStarted, getSourceOpinions, incompleteOpinionUpdateValue, setIncompleteOpinionSelectedPage, toggleSourceOpinionModal } from '../store';
import styles from './UploadOpinions.module.scss';
import { OpinionCommissionedBy, OpinionScope } from '../../../admin/opinions/store';
import { ArrowRight } from '../../../shared/icons';
import { IconButton } from '../../../shared/button/IconButton';
import { InformationTooltip } from '../../../shared/tooltip';
import { getNumberOfPages } from '../../../documents/document-viewer/store';
import { OpinionLinkButton } from '../../../shared/button/LinkBespokeOpinionButton';
import { OpinionLinkModal } from './OpinionLinkModal';
import { LongText } from '../../../shared/longtext/LongText';
import { OpinionDetailsScope } from './OpinionDetailsScope';

const { amber, primary } = styles;

interface OpinionDetailsProps {
    opinion: ArkOpinion;
}

export const OpinionDetails: React.FC<OpinionDetailsProps> = ({ opinion }) => {
    const dispatch = useAppDispatch();
    const dropdownLists = useAppSelector(getAllDropdownLists);
    const numPages = useAppSelector(getNumberOfPages);
    const sourceOpinions = useAppSelector(getSourceOpinions);

    const hasIndustryStandardPermission = useAppSelector(getUserHasFeaturePermissionNoAdmin([FeaturePermission.MANAGE_INDUSTRY_STANDARD_OPINIONS]));
    const { description, jurisdiction, commissionedBy, commissionedByIfOther, scope, type, dateOfOpinion, bespoke, subjectOfOpinion, background, focus, counterpartyEndPage, counterpartyStartPage, sourceOpinionIds, additionalScopes } = opinion;

    const bespokeOptions = useMemo(() => {
        const allOptions = [{ label: 'Bespoke', value: '1' }, { label: 'Industry Standard', value: '0' }];
        if (hasIndustryStandardPermission) {
            return allOptions;
        }
        return allOptions.filter(({ label }) => label === 'Bespoke');
    }, [hasIndustryStandardPermission]);

    const bespokeValue = useMemo(() => bespokeOptions.find(({ value }) => parseInt(value) === bespoke) || { label: 'Bespoke', value: '1' }, [bespokeOptions, bespoke]);

    const bespokeOpinion = useMemo(() => bespoke === 1, [bespoke]);

    const updateDropdownValue = useCallback((key: keyof ArkOpinion, dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(dropdownValue)) {
            value = (dropdownValue as DropdownOption).value;
        }
        dispatch(incompleteOpinionUpdateValue(key, value));
        if (key === 'scope' && value === OpinionScope.GMRA_GMSLA_NETTING && bespoke === 0) {
            dispatch(incompleteOpinionUpdateValue('commissionedBy', OpinionCommissionedBy.ICMA));
        }
    }, [bespoke, dispatch]);

    const updateDateValue = useCallback((key: keyof ArkOpinion, value: Date | null) => {
        const date = value ? formatDate(value, UTC_FORMAT) : null;
        dispatch(incompleteOpinionUpdateValue(key, date));
    }, [dispatch]);

    const getDropdownListOptions = useCallback((listName: string) => {
        const dropdownList = dropdownLists.find(({ name }) => name === listName);
        return dropdownList ? dropdownList.options.map(type => ({ value: type, label: type })) : [];
    }, [dropdownLists]);

    const getDropdownValue = useCallback((initialValue: string | null, options: DropdownOption[]) => {
        if (!initialValue) {
            return null;
        }
        return options.find(({ value }) => value === initialValue) || null;
    }, []);

    const opinionDateValue = useMemo(() => dateOfOpinion ? new Date(dateOfOpinion) : null, [dateOfOpinion]);
    const setOpinionDate = useCallback((value: Date | null) => updateDateValue('dateOfOpinion', value), [updateDateValue]);

    const setOpinionDescription = useCallback((description: string) => dispatch(incompleteOpinionUpdateValue('description', description)), [dispatch]);
    const setSubjectOfOpinion = useCallback((subjectOfOpinion: string) => dispatch(incompleteOpinionUpdateValue('subjectOfOpinion', subjectOfOpinion)), [dispatch]);
    const setBackground = useCallback((background: string) => dispatch(incompleteOpinionUpdateValue('background', background)), [dispatch]);

    const jurisdictionOptions = useMemo(() => getDropdownListOptions('OpinionJurisdiction'), [getDropdownListOptions]);
    const jurisdictionValue = useMemo(() => getDropdownValue(jurisdiction, jurisdictionOptions), [jurisdiction, getDropdownValue, jurisdictionOptions]);

    const setJurisdiction = useCallback((value: DropdownOption | Options<DropdownOption> | null) => updateDropdownValue('jurisdiction', value), [updateDropdownValue]);

    const focusOptions = useMemo(() => getFocusOptions(jurisdiction), [jurisdiction]);
    const focusValue = useMemo(() => getDropdownValue(focus, focusOptions), [focus, getDropdownValue, focusOptions]);

    const setFocus = useCallback((value: DropdownOption | Options<DropdownOption> | null) => updateDropdownValue('focus', value), [updateDropdownValue]);
    const setBespokeFocus = useCallback((value: string) => dispatch(incompleteOpinionUpdateValue('focus', value)), [dispatch]);

    const setOpinionType = useCallback((value: DropdownOption | Options<DropdownOption> | null) => updateDropdownValue('type', value), [updateDropdownValue]);

    const opinionTypeOptions = useMemo(() => [
        { label: 'Original', value: 'Original' },
        { label: 'Update', value: 'Update' },
        { label: 'Blackline', value: 'Blackline' },
        { label: 'Full', value: 'Full' },
        { label: 'Supplemental', value: 'Supplemental' },
        { label: 'Consolidated', value: 'Consolidated' },
        { label: 'Consolidated Update', value: 'Consolidated Update' }
    ], []);

    const opinionTypeValue = useMemo(() => opinionTypeOptions.find(({ value }) => value === type) || null, [opinionTypeOptions, type]);

    const sourceOpinionScopes = useMemo(() => sourceOpinions.reduce((acc: OpinionScope[], { opinionId, scope }) => {
        if (sourceOpinionIds?.includes(opinionId) && scope) {
            acc = [...acc, scope];
        }
        return acc;
    }, []), [sourceOpinionIds, sourceOpinions]);

    const commissionedByOptions = useMemo(() => opinionCommissionedByOptions(), []);

    const opinionCommissionedByValue = useMemo(() => commissionedByOptions.find(({ value }) => value === commissionedBy) || null, [commissionedBy, commissionedByOptions]);

    const setOpinionCommissionedByIfOther = useCallback((value: string | null) => dispatch(incompleteOpinionUpdateValue('commissionedByIfOther', value)), [dispatch]);

    const setOpinionCommissionedByDropdown = useCallback((value: DropdownOption | Options<DropdownOption> | null) => {
        let commissionedByIfOther = null;
        if (!isNull(value) && (value as DropdownOption).value === OpinionCommissionedBy.OTHER) {
            commissionedByIfOther = '';
        }
        setOpinionCommissionedByIfOther(commissionedByIfOther);
        updateDropdownValue('commissionedBy', value);
    }, [setOpinionCommissionedByIfOther, updateDropdownValue]);

    const setBespoke = useCallback((option: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(option)) {
            value = parseInt((option as DropdownOption).value);
        }
        dispatch(incompleteOpinionUpdateValue('bespoke', value));
        dispatch(incompleteOpinionUpdateValue('scope', null));
        dispatch(incompleteOpinionUpdateValue('sourceOpinionIds', undefined));
        dispatch(incompleteOpinionUpdateValue('additionalScopes', undefined));
    }, [dispatch]);

    const hideCommissionedBy = useMemo(() => scope === OpinionScope.GMRA_GMSLA_NETTING && bespoke === 0, [scope, bespoke]);

    const counterpartyCoverageStartPage = useMemo(() => counterpartyStartPage || 1, [counterpartyStartPage]);
    const linkToCounterpartyStartPageDisabled = useMemo(() => !isNull(numPages) && counterpartyCoverageStartPage > numPages, [counterpartyCoverageStartPage, numPages]);

    const counterpartyCoverageEndPage = useMemo(() => counterpartyEndPage || numPages, [counterpartyEndPage, numPages]);
    const linkToCounterpartyEndPageDisabled = useMemo(() => isNull(counterpartyCoverageEndPage) || (!isNull(numPages) && !isNull(counterpartyCoverageEndPage) && counterpartyCoverageEndPage > numPages), [counterpartyCoverageEndPage, numPages]);

    const scrollToPage = useCallback((pageNumber: number | null) => dispatch(setIncompleteOpinionSelectedPage(pageNumber)), [dispatch]);
    const updatePageReference = useCallback((key: keyof ArkOpinion, value: string) => {
        if (!isNaN(parseInt(value)) || value === '') {
            dispatch(incompleteOpinionUpdateValue(key, value === '' ? null : parseInt(value)));
            if (value === '') {
                dispatch(setIncompleteOpinionSelectedPage(null));
            }
        }
    }, [dispatch]);

    const updateCounterpartyStartPage = useCallback((value: string) => updatePageReference('counterpartyStartPage', value), [updatePageReference]);
    const updateCounterpartyEndPage = useCallback((value: string) => updatePageReference('counterpartyEndPage', value), [updatePageReference]);

    const showLinkBespokeButton = useMemo(() => !!jurisdiction && bespoke === 1, [jurisdiction, bespoke]);
    const showRemoveOpinionLink = useMemo(() => !isUndefined(sourceOpinionIds) && sourceOpinionIds.length === 1, [sourceOpinionIds]);
    const sourceOpinionDescription = useMemo(() => {
        if (isUndefined(sourceOpinionIds) || sourceOpinionIds.length === 0) {
            return 'View Opinions';
        }
        if (!isUndefined(sourceOpinionIds) && sourceOpinionIds.length === 1) {
            return sourceOpinions.find(({ opinionId }) => sourceOpinionIds.includes(opinionId))!.description;
        }
        return `${sourceOpinionIds.length} Opinions Selected`;
    }, [sourceOpinionIds, sourceOpinions]);

    const openOpinionLinkModal = useCallback(() => {
        dispatch(toggleSourceOpinionModal(true));
        dispatch(fetchSourceOpinionsStarted(1, jurisdiction!));
    }, [dispatch, jurisdiction]);

    const closeOpinionLinkModal = useCallback(() => dispatch(toggleSourceOpinionModal(false)), [dispatch]);

    const selectSourceOpinion = useCallback((value: number) => {

        const currentSourceOpinionIds = sourceOpinionIds || [];
        dispatch(incompleteOpinionUpdateValue('sourceOpinionIds', [...currentSourceOpinionIds, value]));

        const sourceScope = sourceOpinions.find(({ opinionId }) => opinionId === value)!.scope!;
        // if scope is null then we set the scope else we add this to the additional scopes.
        if (isNull(scope)) {
            dispatch(incompleteOpinionUpdateValue('scope', sourceScope));
        }
        if (!isNull(scope) && sourceScope !== scope) {
            // If we are adding a source opinion and the scope is not the current scope then we should add it to the additional ones
            const currentAdditionalScopes = additionalScopes || [];
            const updatedAdditionalScope = uniq([...currentAdditionalScopes, sourceScope]);
            dispatch(incompleteOpinionUpdateValue('additionalScopes', updatedAdditionalScope));
        }
    }, [sourceOpinions, scope, sourceOpinionIds, dispatch, additionalScopes]);

    const deselectSourceOpinion = useCallback((value: number) => {
        const updatedSourceOpinionIds = sourceOpinionIds!.filter(id => id !== value);
        dispatch(incompleteOpinionUpdateValue('sourceOpinionIds', updatedSourceOpinionIds));
        const deletedSourceScope = sourceOpinions.find(({ opinionId }) => opinionId === value)!.scope!;
        // first check if the source opinion scope matches the scope or is an additional scope
        if (scope === deletedSourceScope) {
            // then we need to check if there are additional scopes, if so move one of them from the additional scopes to being the scope
            if (additionalScopes && additionalScopes.length > 0) {
                // move one scope from here to being the scope
                const additionalScopeToUse = additionalScopes[0];
                dispatch(incompleteOpinionUpdateValue('scope', additionalScopeToUse));
                const updatedAdditionalScopes = additionalScopes.filter(scope => scope !== additionalScopeToUse);
                dispatch(incompleteOpinionUpdateValue('additionalScopes', updatedAdditionalScopes));
            } else {
                // change the scope back to null
                dispatch(incompleteOpinionUpdateValue('scope', null));
            }
        } else {
            // then we are removing an additional scope
            const updatedAdditionalScopes = additionalScopes && additionalScopes.filter(scope => scope !== deletedSourceScope);
            dispatch(incompleteOpinionUpdateValue('additionalScopes', updatedAdditionalScopes));
        }
    }, [dispatch, scope, sourceOpinionIds, sourceOpinions, additionalScopes]);

    const removeSourceOpinion = useCallback(() => deselectSourceOpinion(sourceOpinionIds![0]), [deselectSourceOpinion, sourceOpinionIds]);

    const selectDisabled = useCallback((value: number) => {
        const sourceScope = sourceOpinions.find(({ opinionId }) => opinionId === value)!.scope!;
        return sourceOpinionScopes.includes(sourceScope);
    }, [sourceOpinions, sourceOpinionScopes]);

    return (
        <div data-testid='opinion-details-wrapper' className={styles.opinionDetailsWrapper}>
            <Text
                label='Description'
                onChange={e => setOpinionDescription(e.target.value)}
                value={description}
                maxLength={256}
                testId='opinion-description'
                marginBottom='0'
            />
            <div data-testid='bespoke-wrapper' className={styles.inputWrapper}>
                <div className={styles.inputHeader}>Bespoke or Industry Standard?</div>
                <Dropdown
                    value={bespokeValue}
                    options={bespokeOptions}
                    onChange={setBespoke}
                    testId='opinion-bespoke'
                    menuPortalTarget={document.body}
                    isClearable={false}
                />
            </div>
            {bespokeOpinion &&
                <>
                    <Text
                        label='Subject of Opinion'
                        onChange={e => setSubjectOfOpinion(e.target.value)}
                        value={subjectOfOpinion || ''}
                        placeholder='Subject...'
                        maxLength={400}
                        testId='subject-of-opinion'
                        marginBottom='0'
                    />
                    <LongText
                        label='Background'
                        testId='background'
                        onChange={value => setBackground(value)}
                        value={background || ''}
                        placeholder='Background...'
                        maxLength={4000}
                        width='100%'
                    />
                </>
            }
            <div data-testid='jurisdiction-wrapper' className={styles.inputWrapper}>
                <div className={styles.inputHeader}>Jurisdiction</div>
                <Dropdown
                    value={jurisdictionValue}
                    options={jurisdictionOptions}
                    onChange={setJurisdiction}
                    testId='opinion-jurisdiction'
                    menuPortalTarget={document.body}
                />
            </div>
            <div data-testid='focus-wrapper' className={styles.inputWrapper}>
                <div className={styles.inputHeader}>Focus</div>
                {bespoke === 1 ? (
                    <Text
                        onChange={e => setBespokeFocus(e.target.value)}
                        value={focus}
                        maxLength={128}
                        testId='bespoke-opinion-focus'
                        marginBottom='0'
                    />
                ) : (
                    <Dropdown
                        value={focusValue}
                        options={focusOptions}
                        onChange={setFocus}
                        testId='opinion-focus'
                        menuPortalTarget={document.body}
                        isClearable={false}
                    />)}
            </div>
            <div data-testid='opinion-type-wrapper' className={styles.inputWrapper}>
                <div className={styles.inputHeader}>Type</div>
                <Dropdown
                    value={opinionTypeValue}
                    options={opinionTypeOptions}
                    onChange={setOpinionType}
                    testId='opinion-type'
                    menuPortalTarget={document.body}
                />
            </div>
            {showLinkBespokeButton &&
                <OpinionLinkButton
                    open={openOpinionLinkModal}
                    remove={removeSourceOpinion}
                    buttonLabel={sourceOpinionDescription}
                    header='Link to Source Opinion(s)'
                    showRemove={showRemoveOpinionLink}
                />
            }
            <div data-testid='date-of-opinion-wrapper' className={styles.inputWrapper}>
                <div className={styles.inputHeader}>Date of Opinion</div>
                <DatePicker
                    value={opinionDateValue}
                    onChange={setOpinionDate}
                    testId='date-of-opinion'
                    maxDate={new Date()}
                />
            </div>
            <OpinionDetailsScope
                bespoke={bespoke}
                scope={scope}
                additionalScopes={additionalScopes}
                sourceOpinionScopes={sourceOpinionScopes}
            />
            {!hideCommissionedBy && <div data-testid='opinion-commissioned-by-wrapper' className={styles.inputWrapper}>
                <div className={styles.inputHeader}>Commissioned by</div>
                <Dropdown
                    value={opinionCommissionedByValue}
                    options={commissionedByOptions}
                    onChange={setOpinionCommissionedByDropdown}
                    menuPortalTarget={document.body}
                    testId='opinion-commissioned-by'
                />
                {commissionedBy === OpinionCommissionedBy.OTHER &&
                    <div data-testid='opinion-commissioned-by-other-wrapper' className={styles.inputWrapper}>
                        <Text
                            label='Commissioned by other'
                            onChange={e => setOpinionCommissionedByIfOther(e.target.value)}
                            value={commissionedByIfOther || ''}
                            maxLength={256}
                            testId='opinion-commissioned-by'
                            marginBottom='0'
                        />
                    </div>
                }
            </div>
            }
            <div className={styles.counterpartyPageInfo}>
                <div className={styles.counterpartyPageWrapper} style={{ marginRight: '10px' }}>
                    <div className={styles.counterpartyPageLabelWrapper}>
                        <div className={styles.counterpartyPageLabel}>Counterparty Annex Start Page</div>
                        <div className={styles.counterpartyPageTooltip}>
                            <InformationTooltip content='By providing the counterparty annex page details, we can attempt to extract the information from this table automatically' />
                        </div>
                    </div>
                    <div className={styles.counterpartyPageInput}>
                        <Text
                            testId='counterparty-start-page'
                            value={counterpartyStartPage?.toString()}
                            onChange={e => updateCounterpartyStartPage(e.target.value)}
                            marginBottom='0px'
                            width='40%'
                            marginRight='10px'
                            showBoldPlaceholder
                            borderColour={isNull(counterpartyStartPage) ? amber : primary}
                        />
                        <IconButton icon={ArrowRight} onClick={() => scrollToPage(counterpartyCoverageStartPage)} disabled={linkToCounterpartyStartPageDisabled} testId='counterparty-start-page' />
                    </div>
                </div>
                <div className={styles.counterpartyPageWrapper}>
                    <div className={styles.counterpartyPageLabelWrapper}>
                        <div className={styles.counterpartyPageLabel}>Counterparty Annex End Page</div>
                    </div>
                    <div className={styles.counterpartyPageInput}>
                        <Text
                            testId='counterparty-end-page'
                            value={counterpartyEndPage?.toString()}
                            onChange={e => updateCounterpartyEndPage(e.target.value)}
                            marginBottom='0px'
                            width='40%'
                            marginRight='10px'
                            showBoldPlaceholder
                            borderColour={isNull(counterpartyEndPage) ? amber : primary}
                        />
                        <IconButton icon={ArrowRight} onClick={() => scrollToPage(counterpartyCoverageEndPage)} disabled={linkToCounterpartyEndPageDisabled} testId='counterparty-end-page' />
                    </div>
                </div>
            </div>
            <OpinionLinkModal
                select={selectSourceOpinion}
                deselect={deselectSourceOpinion}
                selectDisabled={selectDisabled}
                close={closeOpinionLinkModal}
                sourceOpinionIds={sourceOpinionIds}
                sourceOpinions={sourceOpinions}
            />
        </div>
    );
};
